import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@morressier/alembic';
import { fetchNextWorkspaceSubmission } from 'src/api/queries';
import { SubmissionNavContext } from 'src/utils/submissionsList';

type SubmissionDetailsLocationState = {
  from?: string;
  parentFrom?: string;
  submissionNav?: SubmissionNavContext;
};

/**
 * Navigates to the next submission in the workspace submissions list the user
 * came from.
 *
 * The backend resolves "next" by index within the filtered list, so the list
 * context has to be handed over through router state by the submissions list
 * page. Without it (deep links, AppHome, archive lists, the non-workspace
 * `/submission/*` mount) the action stays disabled.
 */
export const useNextSubmission = ({
  submissionId,
  activeTab
}: {
  submissionId?: string;
  activeTab: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shortname = '' } = useParams();
  const toast = useToast();
  const [isNextLoading, setIsNextLoading] = useState(false);

  const state =
    (location.state as SubmissionDetailsLocationState | null) ?? null;
  const submissionNav = state?.submissionNav;

  // The backend wraps the next index back to 0 once the last submission of the
  // filtered list is reached, so "Next" is only meaningful before that point.
  const isLastInList =
    typeof submissionNav?.total === 'number' &&
    submissionNav.index >= submissionNav.total - 1;

  const canGoNext =
    Boolean(submissionNav && submissionId && shortname) && !isLastInList;

  const goNext = useCallback(async () => {
    if (!submissionNav || !submissionId || isNextLoading || isLastInList)
      return;

    setIsNextLoading(true);
    try {
      const { data } = await fetchNextWorkspaceSubmission({
        workspaceId: submissionNav.workspaceId,
        submissionId,
        index: submissionNav.index,
        offset: submissionNav.offset,
        limit: submissionNav.limit,
        filters: submissionNav.filters,
        sort: submissionNav.sort
      });

      // `nextGlobalIndex` 0 means the backend wrapped around to the first item
      // (end of the list, or the list shifted while the user was reading), which
      // is not a "next submission".
      if (
        !data?.nextItem ||
        typeof data.nextGlobalIndex !== 'number' ||
        data.nextGlobalIndex === 0
      ) {
        toast({
          variant: 'error',
          title: 'No next submission available',
          isClosable: true
        });
        return;
      }

      const nextItem = data.nextItem;

      navigate(
        `/app/workspace/${shortname}/j/${nextItem.submissionContextId}/submissions?selectedItem=${nextItem.id}&activeTab=${activeTab}`,
        {
          // Keep `from` so "Back" still returns to the list rather than chaining
          // through every submission visited with this button.
          state: {
            ...(state ?? {}),
            submissionNav: {
              index: data.nextGlobalIndex,
              total: submissionNav.total,
              offset: data.nextOffset,
              limit: data.nextLimit,
              workspaceId: submissionNav.workspaceId,
              filters: submissionNav.filters,
              sort: submissionNav.sort
            }
          }
        }
      );
    } catch {
      toast({
        variant: 'error',
        title: 'Unable to load the next submission',
        isClosable: true
      });
    } finally {
      setIsNextLoading(false);
    }
  }, [
    submissionNav,
    submissionId,
    isNextLoading,
    isLastInList,
    shortname,
    activeTab,
    state,
    toast,
    navigate
  ]);

  return { canGoNext, isNextLoading, goNext };
};
